/** The typed edge of the API. Every call goes through here, so the shapes the
 *  server returns are written down in exactly one place. */

export interface User {
  id: number;
  email: string;
  name: string;
  jobTitle: string | null;
  bio: string | null;
  avatarKey: string | null;
  notifyPref: 'all' | 'digest' | 'none';
  locale: 'en' | 'pt';
  timezone: string;
  isStaff: boolean;
  isAdmin: boolean;
  isActive: boolean;
  /** When they were last here. null means never. */
  lastSeenAt: string | null;
}

export interface Workspace {
  id: number;
  name: string;
  detailsHtml?: string | null;
  isActive: boolean;
  projectCount: number;
}

export type BillingMethod = 'prepaid' | 'recurring' | 'occasional';
/** Two states, because a draft of an invoice never becomes an invoice here:
 *  the certified software issues that. Open is work in hand; closed is handed
 *  over — invoiced over there, or sent. */
export type DocumentState = 'open' | 'closed';
export type DocumentKind = 'invoice' | 'statement';

export interface Billable {
  projectId: number;
  projectTitle: string;
  workspaceId: number;
  workspaceName: string;
  billingMethod: BillingMethod;
  billingPeriodMonths: number;
  hourlyRateCents: number | null;
  minutes: number;
  amountCents: number | null;
  oldestDay: string | null;
  newestDay: string | null;
  packageHours: number | null;
  usedHours: number;
}

export interface DocumentLine {
  id: number;
  projectId: number;
  description: string;
  minutes: number;
  rateCents: number;
  amountCents: number;
}

export interface Document {
  id: number;
  workspaceId: number;
  workspaceName: string;
  kind: DocumentKind;
  state: DocumentState;
  periodStart: string | null;
  periodEnd: string | null;
  totalCents: number;
  note: string | null;
  externalRef: string | null;
  createdBy: string | null;
  createdAt: string;
  closedBy: string | null;
  closedAt: string | null;
  lines?: DocumentLine[];
  entries: number;
}

export type ProjectStatus =
  | 'planning' | 'in_progress' | 'on_hold' | 'complete' | 'archived' | 'placeholder';

export interface Project {
  id: number;
  workspaceId: number;
  workspaceName: string;
  title: string;
  descriptionHtml?: string | null;
  status: ProjectStatus;
  totalHours: number | null;
  usedHours: number;
  remainingHours: number | null;
  startDate: string | null;
  endDate: string | null;
  managerId: number | null;
  managerName: string | null;
  ownerId: number | null;
  ownerName: string | null;
  isPublished: boolean;
  openTasks: number;
  billingMethod: BillingMethod;
  billingPeriodMonths: number;
  hourlyRateCents: number | null;
  createdAt: string;
  updatedAt: string;
}

export type TaskState =
  | 'new' | 'in_progress' | 'waiting_client' | 'in_stage' | 'closed';
export type Priority = 'low' | 'medium' | 'high';

export interface Task {
  id: number;
  projectId: number;
  projectTitle: string;
  workspaceId: number;
  title: string;
  descriptionHtml?: string | null;
  state: TaskState;
  priority: Priority;
  assigneeId: number | null;
  assigneeName: string | null;
  createdById: number | null;
  createdByName: string | null;
  commentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TaskEvent {
  id: number;
  actorId: number | null;
  actorName: string | null;
  fromState: TaskState | null;
  toState: TaskState;
  createdAt: string;
}

export interface Comment {
  id: number;
  taskId: number;
  authorId: number | null;
  authorName: string | null;
  bodyHtml: string;
  /** Seen by the team only; the server never sends one to a client. */
  internal: boolean;
  createdAt: string;
}

export interface TimeEntry {
  id: number;
  projectId: number;
  projectTitle: string;
  workspaceName: string;
  userId: number | null;
  userName: string | null;
  title: string | null;
  notes: string | null;
  workedOn: string;
  minutes: number;
  documentId: number | null;
  /** Whether anybody still has to act on that document: open holds the entry
   *  still, closed does not. */
  documentState: DocumentState | null;
}

export interface Attachment {
  id: number;
  ownerType: string;
  ownerId: number;
  filename: string;
  mime: string | null;
  sizeBytes: number | null;
  createdAt: string;
}

export interface ManagedUser extends User {
  projectIds: number[];
}

export interface MonthTotal {
  month: string;
  hours: number;
}

export interface Summary extends Workspace {
  projectsByStatus: Record<string, number>;
  openTasks: number;
  hoursBought: number;
  hoursUsed: number;
  lastWorkedOn: string | null;
  members: User[];
  byMonth: MonthTotal[];
}

export interface DayTotal {
  day: string;
  minutes: number;
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, body?.error ?? response.statusText);
  }
  return body as T;
}

const get = <T,>(path: string) => request<T>(path);
const send = <T,>(method: string, path: string, body?: unknown) =>
  request<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });

export const api = {
  requestLink: (email: string, next: string | null) =>
    send<{ message: string }>('POST', '/api/auth/magic-link', { email, next: next ?? '' }),
  inspectLink: (token: string) =>
    get<{ valid: boolean; name?: string; email?: string; reason?: string }>(
      `/api/auth/verify?token=${encodeURIComponent(token)}`),
  redeemLink: (token: string) => send<User>('POST', '/api/auth/verify', { token }),
  logout: () => send<void>('POST', '/api/auth/logout'),

  me: () => get<{ user: User; isStaff: boolean }>('/api/me'),
  updateMe: (patch: Partial<Pick<User, 'name' | 'jobTitle' | 'bio' | 'notifyPref' | 'locale' | 'timezone'>>) =>
    send<User>('PATCH', '/api/me', patch),
  myTime: (days = 14) => get<DayTotal[]>(`/api/me/time?days=${days}`),
  myTasks: (state?: TaskState) => get<Task[]>(`/api/me/tasks${state ? `?state=${state}` : ''}`),

  workspaces: () => get<Workspace[]>('/api/workspaces'),
  // A client's people are what its projects add up to; membership itself is
  // a project's, which is where it is added and removed.
  workspacePeople: (id: number) => get<User[]>(`/api/workspaces/${id}/people`),
  projectPeople: (id: number) => get<User[]>(`/api/projects/${id}/people`),
  addProjectMember: (projectId: number, userId: number) =>
    send<void>('POST', `/api/projects/${projectId}/members`, { userId }),
  removeProjectMember: (projectId: number, userId: number) =>
    send<void>('DELETE', `/api/projects/${projectId}/members/${userId}`),
  workspaceSummary: (id: number) => get<Summary>(`/api/workspaces/${id}/summary`),
  createWorkspace: (input: { name: string; detailsHtml?: string }) =>
    send<Workspace>('POST', '/api/workspaces', input),
  updateWorkspace: (id: number, patch: Partial<{ name: string; detailsHtml: string; isActive: boolean }>) =>
    send<Workspace>('PATCH', `/api/workspaces/${id}`, patch),

  projects: (params: { workspaceId?: number; status?: string } = {}) =>
    get<Project[]>(`/api/projects${query(params)}`),
  project: (id: number) => get<Project>(`/api/projects/${id}`),
  projectTasks: (id: number) => get<Task[]>(`/api/projects/${id}/tasks`),
  projectTime: (id: number) => get<TimeEntry[]>(`/api/projects/${id}/time`),
  projectFiles: (id: number) => get<Attachment[]>(`/api/projects/${id}/files`),

  task: (id: number) => get<Task>(`/api/tasks/${id}`),
  createTask: (input: {
    projectId: number; title: string; descriptionHtml?: string;
    priority?: Priority; assigneeId?: number;
  }) => send<Task>('POST', '/api/tasks', input),
  updateTask: (id: number, patch: Partial<{ title: string; descriptionHtml: string; state: TaskState; priority: Priority; assigneeId: number }>) =>
    send<Task>('PATCH', `/api/tasks/${id}`, patch),
  approveTask: (id: number) => send<Task>('POST', `/api/tasks/${id}/approve`),
  taskEvents: (id: number) => get<TaskEvent[]>(`/api/tasks/${id}/events`),
  taskFiles: (id: number) => get<Attachment[]>(`/api/tasks/${id}/files`),
  comments: (id: number) => get<Comment[]>(`/api/tasks/${id}/comments`),
  addComment: (id: number, bodyHtml: string, internal = false) =>
    send<Comment>('POST', `/api/tasks/${id}/comments`, { bodyHtml, internal }),
  // Administrators only; the server says so to anybody else.
  deleteComment: (taskId: number, commentId: number) =>
    send<void>('DELETE', `/api/tasks/${taskId}/comments/${commentId}`),

  // The browser sends the file itself to the URL this hands back.
  presignUpload: (input: { filename: string; ownerType: 'project' | 'task'; ownerId: number }) =>
    send<{ key: string; url: string; method: string }>('POST', '/api/uploads', input),

  time: (params: {
    projectId?: number; workspaceId?: number; userId?: number;
    from?: string; to?: string; limit?: number; unbilled?: boolean;
  } = {}) =>
    get<TimeEntry[]>(`/api/time${query({ ...params, unbilled: params.unbilled ? 'true' : undefined })}`),
  logTime: (input: { projectId: number; workedOn: string; minutes: number; title?: string; notes?: string }) =>
    send<TimeEntry>('POST', '/api/time', input),
  deleteTime: (id: number) => send<void>('DELETE', `/api/time/${id}`),
  timeCsvUrl: (params: Record<string, string | number | undefined>) => `/api/time.csv${query(params)}`,

  people: (staffOnly = false) => get<User[]>(`/api/people${staffOnly ? '?staff=true' : ''}`),

  billable: (params: { workspaceId?: number; upTo?: string } = {}) =>
    get<Billable[]>(`/api/billing${query(params)}`),
  documents: (params: { workspaceId?: number; kind?: string; state?: string } = {}) =>
    get<Document[]>(`/api/documents${query(params)}`),
  document: (id: number) => get<Document>(`/api/documents/${id}`),
  draftDocument: (input: {
    workspaceId: number; upTo?: string; note?: string;
    kind?: DocumentKind; method?: BillingMethod; exclude?: number[];
  }) => send<Document>('POST', '/api/documents', input),
  closeDocument: (id: number, externalRef?: string) =>
    send<Document>('POST', `/api/documents/${id}/close`, { externalRef: externalRef ?? '' }),
  reopenDocument: (id: number) => send<Document>('POST', `/api/documents/${id}/reopen`),
  discardDocument: (id: number) => send<void>('DELETE', `/api/documents/${id}`),

  users: (params: { workspaceId?: number; q?: string; includeInactive?: boolean } = {}) =>
    get<ManagedUser[]>(`/api/users${query({
      workspaceId: params.workspaceId,
      q: params.q,
      includeInactive: params.includeInactive ? 'true' : undefined,
    })}`),
  createUser: (input: {
    email: string; name: string; jobTitle?: string;
    isStaff?: boolean; projectIds?: number[];
  }) => send<User>('POST', '/api/users', input),
  updateUser: (id: number, patch: Partial<{
    name: string; jobTitle: string; isStaff: boolean; isAdmin: boolean;
    isActive: boolean; projectIds: number[];
  }>) => send<User>('PATCH', `/api/users/${id}`, patch),

  createProject: (input: {
    workspaceId: number; title: string; descriptionHtml?: string;
    status?: ProjectStatus; totalHours?: number | null; startDate?: string | null;
    endDate?: string | null; managerId?: number | null; ownerId?: number | null;
    isPublished?: boolean;
    billingMethod?: BillingMethod; billingPeriodMonths?: number;
    hourlyRateCents?: number | null;
  }) => send<Project>('POST', '/api/projects', input),
  // null empties a field and leaving it out keeps it: an emptied box has to
  // travel as null, because undefined does not survive JSON.stringify and the
  // cleared value came straight back.
  updateProject: (id: number, patch: Partial<{
    title: string; descriptionHtml: string; status: ProjectStatus;
    totalHours: number | null; startDate: string | null; endDate: string | null;
    managerId: number | null; ownerId: number | null; isPublished: boolean;
    billingMethod: BillingMethod; billingPeriodMonths: number;
    hourlyRateCents: number | null;
  }>) => send<Project>('PATCH', `/api/projects/${id}`, patch),
  updateTime: (id: number, patch: Partial<{
    title: string; notes: string; workedOn: string; minutes: number;
  }>) => send<TimeEntry>('PATCH', `/api/time/${id}`, patch),
};

function query(params: Record<string, string | number | undefined>): string {
  const pairs = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (pairs.length === 0) return '';
  return '?' + pairs.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}
